import React, { useRef, useState, useCallback, useEffect } from "react";

/**
 * BeforeAfter — drag-to-reveal image comparison slider.
 * Props: beforeSrc, afterSrc, beforeLabel, afterLabel
 */
export const BeforeAfter = ({
  beforeSrc,
  afterSrc,
  beforeLabel = "BEFORE",
  afterLabel = "AFTER",
  beforeAlt = "",
  afterAlt = "",
}) => {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50); // %
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      move(cx);
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [move]);

  const start = (clientX) => {
    dragging.current = true;
    move(clientX);
  };

  return (
    <div
      ref={wrapRef}
      data-testid="before-after"
      className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-white/[0.08] select-none cursor-ew-resize bg-[#0B0C12]"
      onMouseDown={(e) => start(e.clientX)}
      onTouchStart={(e) => start(e.touches[0].clientX)}
    >
      {/* AFTER (full layer) */}
      <img
        src={afterSrc}
        alt={afterAlt}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-md bg-brand-orange/90 text-white text-[10px] font-mono uppercase tracking-[0.22em]">
        {afterLabel}
      </div>

      {/* BEFORE (clipped layer) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={beforeSrc}
          alt={beforeAlt}
          draggable={false}
          className="absolute inset-0 h-full w-auto max-w-none object-cover"
          style={{ width: `${wrapRef.current?.clientWidth || 0}px` }}
        />
        <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-md bg-white/15 backdrop-blur text-white text-[10px] font-mono uppercase tracking-[0.22em]">
          {beforeLabel}
        </div>
      </div>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 z-20"
        style={{ left: `calc(${pos}% - 1px)` }}
      >
        <div className="w-[2px] h-full bg-brand-orange/95 shadow-[0_0_12px_rgba(255,106,0,0.7)]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-0 w-10 h-10 rounded-full bg-brand-orange shadow-[0_0_24px_rgba(255,106,0,0.6)] flex items-center justify-center text-white text-xs font-bold">
          <span className="-mr-0.5">‹</span>
          <span className="-ml-0.5">›</span>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfter;
